import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

import styled from 'styled-components';
import moment from 'moment';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';
import CalcDate from '../../Model/Share/Normal/CalcDate';

import { ReactComponent as Menu2IconSvg } from '../../image/Menu2.svg';
import { ReactComponent as LockIconSvg } from '../../image/Lock.svg';
import { ReactComponent as PlusSvg } from '../../image/Plus.svg';

// 락커 상태 → 표시 메타데이터 //
function getLockerMeta(data, t) {
  if (data.state === 'Empty') {
    return {
      isExpired: false,
      days: '',
      displayState: t('main.locker.empty'),
      btnText: t('main.locker.startUsage')
    };
  }
  if (data.state === 'Using') {
    const isExpired = !moment(data.endDate).isAfter(moment())
      && moment(data.endDate).format('YYYY.MM.DD') !== moment().format('YYYY.MM.DD');

    return {
      isExpired,
      days: CalcDate.getDday(moment(data.endDate).format('YYYY-MM-DDT00:00:00')),
      displayState: isExpired ? t('main.locker.overused') : t('main.locker.inUse'),
      btnText: t('main.locker.endUsage')
    };
  }
  if (data.state === 'Invalid') {
    return {
      isExpired: false,
      days: '',
      displayState: t('main.locker.unavailable'),
      btnText: t('main.locker.unlock')
    };
  }
  if (data.state === 'Expected') {
    return {
      isExpired: false,
      days: '-',
      displayState: t('main.locker.expected'),
      btnText: t('main.locker.unlock')
    };
  }

  return { isExpired: false, days: '', displayState: '', btnText: '' };
}

const LockerInfo = forwardRef((props, ref) => {
  const {
    index,
    lockerIndex,
    data,
    onClickLockerActionFn,
    onClickUsageHistoryFn,
    onClickChangeStateFn,
    onClickDeleteLockerFn,
    onClickChangeNameFn,
    onClickExtendFn,
    onClickModifyFn
  } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [clickedMore, setClickedMore] = useState(false);

  const MoreButtonRef = useRef();
  const MoreBoxRef = useRef();
  const HistoryRef = useRef();
  const TextRef = useRef();
  const LockRef = useRef();
  const DeleteRef = useRef();
  const ChangeRef = useRef();
  const MessageRef = useRef();
  const ExtendRef = useRef();
  const ModifyRef = useRef();

  useImperativeHandle(ref, () => ({
    setClickedMore: (val) => {
      setClickedMore(val);
    },
    getMoreButtonRef: () => {
      return MoreButtonRef.current;
    },
    getMoreBoxRef: () => {
      return MoreBoxRef.current;
    }
  }));

  // 락커 상태 기반 표시 메타데이터 //
  const { isExpired, days, displayState, btnText } = getLockerMeta(data, t);

  // 더보기 메뉴 표시 제어 //
  useEffect(() => {
    if (clickedMore) {
      MoreBoxRef.current.style.visibility = 'visible';
      MoreBoxRef.current.style.border = `1px solid ${theme.primary}`;
      if (data.state === 'Empty') MoreBoxRef.current.style.height = `36px`;
      else if (data.state === 'Using') MoreBoxRef.current.style.height = `144px`;
      else if (data.state === 'Expected') MoreBoxRef.current.style.height = `36px`;
      else MoreBoxRef.current.style.height = `108px`;
    }
    else {
      MoreBoxRef.current.style.visibility = 'hidden';
      MoreBoxRef.current.style.border = `0px solid ${theme.primary}`;
      MoreBoxRef.current.style.boxShadow = 'none';
      MoreBoxRef.current.style.height = `0px`;
    }
  }, [clickedMore, data, theme])

  // 마우스 클릭 감지 리스너 //
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  // 마우스 커서가 락커가 아닌곳을 클릭할 경우 체크 //
  function onClickOutSide(e) {
    // MoreButtonRef와 MoreBoxRef는 contains로 자식 요소 클릭도 체크
    if (MoreBoxRef.current && !MoreBoxRef.current.contains(e.target)
      && MoreButtonRef.current && !MoreButtonRef.current.contains(e.target)
      && HistoryRef.current !== e.target
      && TextRef.current !== e.target
      && LockRef.current !== e.target
      && DeleteRef.current !== e.target
      && ChangeRef.current !== e.target
      && MessageRef.current !== e.target
      && ExtendRef.current !== e.target
      && ModifyRef.current !== e.target) {
      setClickedMore(false);
    }
  }

  // 더보기 버튼 클릭시 //
  function onClickMoreButton() {
    if (!clickedMore) {
      setClickedMore(true);
    }
  }

  // 사용자 이름 및 D-day클릭시 //
  function onClickMidLineWrapper() {
    if (data.memberId === 0) return;

    window.open('/member/valid/detail/' + data.memberId + '/locker', '_blank');
  }

  // 락커 이름을 "이름 + 좌표 suffix(A-1 같은)"로 분리 //
  function parseLockerName(name) {
    const match = (name || '').match(/^(.*?)(\s+[A-Z]-\d+)$/);
    if (match) return { prefix: match[1], suffix: match[2] };
    return { prefix: name || '', suffix: '' };
  }

  // D-Day 검사 //
  function checkDday() {
    if (days === '') return '';
    else if (days === '-') return;
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day') > 0) return 'D-' + moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day');
    else if (moment(data.endDate).format('YYYY.MM.DD') === moment().format('YYYY.MM.DD')) return 'D-Day';
    else return 'D+' + moment(moment().format('YYYY-MM-DD')).diff(moment(moment(data.endDate).format('YYYY-MM-DD')), 'day');
  }

  return (
    <TopLevelWrapper>
      <InfoBox $state={data.state} $isExpired={isExpired}>
        {
          data.state !== 'Invalid' &&
          <InfoBoxInnerWrapper>
            <TopWrapper>
              <TopLineWrapper>
                <NumberWrapper>
                  <Number>{parseLockerName(data.lockerName).prefix}</Number>
                  {parseLockerName(data.lockerName).suffix && <NumberSuffix>{parseLockerName(data.lockerName).suffix}</NumberSuffix>}
                </NumberWrapper>
                <Menu2Icon ref={MoreButtonRef} onClick={onClickMoreButton} />
              </TopLineWrapper>
              {
                data.state !== 'Empty' &&
                <MidLineWrapper onClick={onClickMidLineWrapper}>
                  <NameWrapper>
                    <Name>{data.userName}</Name>
                  </NameWrapper>
                  <Dday $isOverused={isExpired}>{checkDday()}</Dday>
                </MidLineWrapper>
              }
            </TopWrapper>
            <BottomLineWrapper>
              {
                data.state === 'Invalid' &&
                <LockIcon />
              }
              {
                data.state !== 'Empty' ?
                <State $state={data.state} $isExpired={isExpired}>{displayState}</State>
                :
                <LockerUsagePlusButton onClick={() => onClickLockerActionFn(index, lockerIndex)}>
                  <PlusIcon />
                </LockerUsagePlusButton>
              }
            </BottomLineWrapper>
          </InfoBoxInnerWrapper>
        }
      </InfoBox>
      <MoreBox ref={MoreBoxRef}>
        {
          data.state === 'Expected' ?
          <MoreButton ref={ModifyRef} onClick={() => onClickLockerActionFn(index, lockerIndex)}>{t('memberDetail.btnModifyInfo')}</MoreButton>
          :
          <>
            <MoreButton ref={HistoryRef} onClick={() => onClickUsageHistoryFn(index, lockerIndex)}>{t('main.locker.usageHistory')}</MoreButton>
            {
              data.state !== 'Empty' &&
              <MoreButton ref={TextRef} onClick={() => onClickLockerActionFn(index, lockerIndex)}>{btnText}</MoreButton>
            }
            {
              data.state === 'Using' &&
              <MoreButton ref={ExtendRef} onClick={() => onClickExtendFn(index, lockerIndex)}>{t('main.locker.extendUsage')}</MoreButton>
            }
            {
              data.state === 'Using' &&
              <MoreButton ref={ModifyRef} onClick={() => onClickModifyFn(index, lockerIndex)}>{t('memberDetail.btnModifyInfo')}</MoreButton>
            }
          </>
        }
      </MoreBox>
    </TopLevelWrapper>
  )
});

export default LockerInfo;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px;

  box-sizing: border-box;
  width: 122px;
  height: 122px;

  border: ${props => {
    if (props.$state === 'Invalid') return 'none';
    if (props.$state === 'Using' && props.$isExpired) return `1px solid ${props.theme.pointRed}`;
    return `1px solid ${props.theme.line}`;
  }};
  border-radius: 12px;

  transition: all 0.3s ease-in-out;
`;

const InfoBoxInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  
  width: 100%;
  height: 100%;
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const TopLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 15px;

  gap: 5px;

  overflow: hidden;
`;

const NumberWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const Number = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  @media (hover: hover) {
    &:hover {
      cursor: default;
    }
  }
`;

const NumberSuffix = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  flex-shrink: 0;
  white-space: nowrap;
`;

const Menu2Icon = styled(Menu2IconSvg)`
  width: 4px;
  height: 13px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.subText};
    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;

      & path {
        fill: ${props => props.theme.primary};
      }
    }
  }
`;

const MidLineWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  align-items: flex-start;

  margin-top: 6px;

  width: 100%;
  height: 46px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 20px;

  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Name = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Dday = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.$isOverused ? props.theme.pointRed : props.theme.text};

  margin: 0 0 0 0;
`;

const BottomLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const LockIcon = styled(LockIconSvg)`
  width: 11px;
  height: 14px;

  flex-shrink: 0;
  margin-right: 4px;

  & path {
    fill: ${props => props.theme.pointRed};
  }
`;

const State = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => {
    if (props.$state === 'Using' && props.$isExpired) return props.theme.pointRed;
    if (props.$state === 'Using') return props.theme.subKey;
    if (props.$state === 'Invalid') return props.theme.pointRed;
    return props.theme.text;
  }};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: default;
    }
  }
`;

const PlusIcon = styled(PlusSvg)`
  width: 40px;
  height: 40px;

  flex-shrink: 0;

  color: ${props => props.theme.line};
`;

const LockerUsagePlusButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 77px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};
`;

const MoreBox = styled.div`
  position: absolute;
  z-index: 10;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  justify-content: center;
  align-items: center;

  width: 78px;
  height: 0px;

  margin-top: 35px;
  margin-left: 60px;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: none;

  visibility: hidden;

  transition: all 0.3s ease-in-out;
`;

const MoreButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 78px;
  height: 36px;

  background-color: ${props => props.theme.subBackground};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.primary};

  margin: 0 0 0 0;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};
      color: ${props => props.theme.white};
    }
  }
`;
